import { formatMoney } from 'accounting'

import {
  Box, Button, LoadingBubbles, SidebarModal, Text,
} from '@domain-group/suit-theme'

import ProductOptionsSelector from '@components/ProductOptionsSelector'
import ProviderLogo from '@components/ProviderLogo'

import CategorySelector from '@modals/NewOrderModal/components/CategorySelector'
import ProductSelector from '@modals/NewOrderModal/components/ProductSelector'
import ProviderSelector from '@modals/NewOrderModal/components/ProviderSelector'
import RequiredCampaignFields from '@modals/NewOrderModal/components/RequiredCampaignFields'
import ScreenContent from '@modals/NewOrderModal/components/ScreenContent'
import ScreenHeader from '@modals/NewOrderModal/components/ScreenHeader'
import TagSelector from '@modals/NewOrderModal/components/TagSelector'
import ToggleFavouriteOrderButton from '@modals/NewOrderModal/components/ToggleFavouriteOrderButton'

import type { UseNewOrderPayload } from '@modals/NewOrderModal'

import type { DefaultQuoteItemModel } from '@models/types'

import { BuildStandardOrderPayload } from '../../functions/buildStandardOrder'

type AfterActionAndCloseModalParams = {
  closeModal: () => void,
  createOrEditDefaultQuoteItem: (payload: Record<string, DefaultQuoteItemModel>) => void,
  data: DefaultQuoteItemModel,
}

const afterActionAndCloseModal = (params: AfterActionAndCloseModalParams) => {
  const { closeModal, createOrEditDefaultQuoteItem, data } = params

  closeModal()
  if (createOrEditDefaultQuoteItem) createOrEditDefaultQuoteItem({ defaultQuoteItem: data })
}

type BuildAndCreateOrderParams = ReturnType<UseNewOrderPayload['callbacks']['buildStandardOrder']> & {
  callbacks: {
    addItemToOrder: UseNewOrderPayload['callbacks']['addItemToOrder'],
    createCampaignQuoteItem: UseNewOrderPayload['callbacks']['createCampaignQuoteItem'],
    createCampaignQuoteTemplateItem: UseNewOrderPayload['callbacks']['createCampaignQuoteTemplateItem'],
    createDefaultQuoteItem: UseNewOrderPayload['callbacks']['createDefaultQuoteItem'],
    createOrder: UseNewOrderPayload['callbacks']['createOrder'],
  },
  itemType: 'defaultQuoteItem' | 'quoteItem' | 'quoteTemplateItem',
  payload: BuildStandardOrderPayload['payload'],
}

const buildAndCreateOrder = (params: BuildAndCreateOrderParams) => {
  const {
    callbacks: {
      addItemToOrder,
      createCampaignQuoteItem,
      createCampaignQuoteTemplateItem,
      createDefaultQuoteItem,
      createOrder,
    },
    itemType,
    options: {
      existingOrderId,
    },
    payload,
  } = params

  if (itemType === 'quoteItem'){
    // @ts-expect-error TS2345
    return createCampaignQuoteItem(payload)
  }

  if (itemType === 'quoteTemplateItem'){
    // @ts-expect-error TS2345
    return createCampaignQuoteTemplateItem(payload)
  }

  if (itemType === 'defaultQuoteItem'){
    // @ts-expect-error TS2345
    return createDefaultQuoteItem(payload)
  }

  // addItemToOrder
  if (existingOrderId){
    return addItemToOrder(payload)
  }

  // createOrder
  // @ts-expect-error TS2345
  return createOrder(payload)
}

interface StandardFlowProps {
  callbacks: {
    addItem: VoidFunction,
    closeModal: VoidFunction,
  },
  newOrderPayload: UseNewOrderPayload,
}

const StandardFlow = (props: StandardFlowProps) => {
  const {
    callbacks: {
      addItem,
      closeModal,
    },
    newOrderPayload: {
      callbacks: {
        addItemToOrder,
        buildStandardOrder,
        createCampaignQuoteItem,
        createCampaignQuoteTemplateItem,
        createDefaultQuoteItem,
        createOrder,
        createOrEditDefaultQuoteItem,
        resetState,
        selectCategoryId,
        selectProviderId,
        selectProviderPriceListItemId,
        selectProviderPriceListItemOptionValue,
        selectServiceProductId,
        selectTags,
      },
      creating,
      currentPrice: {
        total,
      },
      currentPriceList,
      filteredCategories,
      filteredProviderPriceListItems,
      filteredServiceProducts,
      filteredProviders,
      hasFilteredCategories,
      loadingAgencyPrice,
      loadingApiPricing,
      loadingPriceLists,
      missingCampaignFields,
      selectedAgency,
      selectedCampaign,
      selectedOptionsString,
      selectedPriceListItem,
      selectedProvider,
      selectedService,
      selectedServiceProduct,
      state: {
        selectedCategoryId,
        selectedProviderId,
        selectedProviderPriceListItemId,
        selectedProviderPriceListItemOptions,
        selectedServiceProductId,
        selectedTags,
      },
      validOrder,
    },
  } = props

  const addItemPayload = {
    callbacks: {
      action: (actionParams: ReturnType<UseNewOrderPayload['callbacks']['buildStandardOrder']>) => (
        // @ts-expect-error TS2345
        buildAndCreateOrder({
          ...actionParams,
          callbacks: {
            addItemToOrder,
            createCampaignQuoteItem,
            createCampaignQuoteTemplateItem,
            createDefaultQuoteItem,
            createOrder,
          },
        })
      ),
      afterAction:
        ({ response: { data } }
          : { response: { data: DefaultQuoteItemModel } }) => afterActionAndCloseModal({
          closeModal,
          createOrEditDefaultQuoteItem,
          data,
        }),
      buildActionParams: () => {
        const buildStandardOrderPayload = buildStandardOrder()
        return [buildStandardOrderPayload]
      },
    },
    toastText: 'Item Created',
  }

  return (
    <>
      <ScreenHeader
        callbacks={{
          goBack: () => resetState(),
        }}
        imageComponent={<ProviderLogo provider={selectedProvider} />}
      >
        <ScreenHeader.Title>
          {selectedService.title}
        </ScreenHeader.Title>

        <ScreenHeader.Subtitle>
          {selectedProvider.id ? `from ${selectedProvider.title}` : 'Select Provider'}
        </ScreenHeader.Subtitle>
      </ScreenHeader>

      <ScreenContent>
        {!selectedProviderId && (
          <ProviderSelector
            callbacks={{ selectProvider: provider => selectProviderId(provider.id) }}
            providers={filteredProviders}
          />
        )}

        {!!selectedProviderId && hasFilteredCategories && (
          <CategorySelector
            callbacks={{ selectCategoryId }}
            // @ts-expect-error TS2322
            selectedCategoryId={selectedCategoryId}
            serviceCategories={filteredCategories}
          />
        )}

        {!!selectedProviderId && (!!selectedCategoryId || !hasFilteredCategories) && (
          <ProductSelector
            callbacks={{ selectServiceProductId }}
            // @ts-expect-error TS2322
            currentPriceList={currentPriceList}
            loadingPriceLists={loadingPriceLists}
            // @ts-expect-error TS2322
            selectedServiceProductId={selectedServiceProductId}
            serviceProducts={filteredServiceProducts}
          />
        )}

        {!!missingCampaignFields.length && (
          <RequiredCampaignFields
            campaign={selectedCampaign}
            missingFields={missingCampaignFields}
          />
        )}

        {!!selectedServiceProductId && !missingCampaignFields.length && (
          <ProductOptionsSelector
            callbacks={{
              selectProviderPriceListItemId,
              // @ts-expect-error TS2322
              selectProviderPriceListItemOptionValue,
            }}
            loadingApiPricing={loadingApiPricing}
            providerPriceListItems={filteredProviderPriceListItems}
            quantityFormFieldBoxProps={{ marginTop: 'small' }}
            // @ts-expect-error TS2322
            selectedProviderPriceListItemId={selectedProviderPriceListItemId}
            // @ts-expect-error TS2322
            selectedProviderPriceListItemOptions={selectedProviderPriceListItemOptions}
          />
        )}

        {validOrder && (
          <TagSelector
            boxProps={{ marginTop: 'large' }}
            callbacks={{ updateTags: tags => selectTags(tags) }}
            filters={{ agency_id: selectedAgency.id }}
            selectedTags={selectedTags}
          />
        )}
      </ScreenContent>

      {validOrder && (
        <SidebarModal.Footer>
          <Box flexDirection="column" width="auto">
            <Text color="bodyFontLightColor" fontSize="small">
              Total:
            </Text>

            <Text fontSize="medium" marginTop="small" width="auto">
              {loadingAgencyPrice ? <LoadingBubbles color="blue" /> : formatMoney(total)}
            </Text>
          </Box>

          <Box justifyContent="flex-end" marginLeft="auto" width="auto">
            <ToggleFavouriteOrderButton
              agency={selectedAgency}
              filters={{
                agency_id: selectedAgency.id,
                options_string: selectedOptionsString,
                // @ts-expect-error TS2322
                provider_price_list_item_id: selectedProviderPriceListItemId,
              }}
              orderTotal={total}
              provider={selectedProvider}
              providerPriceListItem={selectedPriceListItem}
              // @ts-expect-error TS2322
              providerPriceListItemOptions={selectedProviderPriceListItemOptions}
              serviceProduct={selectedServiceProduct}
            />

            <Button
              buttonStyle="primaryCreate"
              loading={creating}
              loadingBubbleColor="white"
              // @ts-expect-error TS2554
              onClick={() => addItem(addItemPayload)}
              size="medium"
              //@ts-expect-error TS2820
              width="auto"
            >
              Add Item
            </Button>
          </Box>
        </SidebarModal.Footer>
      )}
    </>
  )
}

export default StandardFlow
